import { useState, useEffect, useRef } from 'react';
import { httpClient } from '@/shared/api/httpClient';

const PING_INTERVAL_MS = 30_000;
const PING_TIMEOUT_MS = 5_000;
const LAST_ONLINE_KEY = 'mi_last_online';

function loadLastOnline(): Date | null {
  const raw = localStorage.getItem(LAST_ONLINE_KEY);
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

async function pingServer(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => { ctrl.abort(); }, PING_TIMEOUT_MS);
    await httpClient.get('/health_check', {
      signal: ctrl.signal,
      headers: { 'Cache-Control': 'no-cache' },
    });
    clearTimeout(timer);
    return true;
  } catch {
    return false;
  }
}

export interface NetworkStatus {
  isOnline: boolean;
  lastOnline: Date | null;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [lastOnline, setLastOnline] = useState<Date | null>(loadLastOnline);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const markOnline = () => {
    const now = new Date();
    setIsOnline(true);
    setLastOnline(now);
    localStorage.setItem(LAST_ONLINE_KEY, now.toISOString());
  };

  const markOffline = () => {
    setIsOnline(false);
  };

  useEffect(() => {
    const onOnline = () => { markOnline(); };
    const onOffline = () => { markOffline(); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const doPing = async () => {
      const ok = await pingServer();
      if (ok) markOnline(); else markOffline();
    };

    void doPing();
    timerRef.current = setInterval(() => { void doPing(); }, PING_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, []);

  return { isOnline, lastOnline };
}
